import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import {
  ArrowLeftIcon,
  PlusIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  PhoneIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import SearchableSelect from './shared/SearchableSelect';
import logger from '../../../utils/logger';

const DEPARTMENTS = ['all', 'hr', 'finance', 'it', 'sales', 'support', 'legal', 'operations', 'marketing', 'management', 'general'];

const AIAgents = ({ showNotification }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [newAgent, setNewAgent] = useState({
    agentName: '',
    userId: '',
    department: 'support',
    roleKey: '',
    description: ''
  });
  // Role catalog (department × role → JD) for the Role select; fetched once,
  // filtered client-side by the chosen department.
  const [roleCatalog, setRoleCatalog] = useState([]);

  useEffect(() => {
    fetchAgents();
    fetchUsers();
    fetchRoleCatalog();
    // Deep-link from business-setup ("hire your AI employee") opens the create
    // flow straight away, then strips the param so a refresh doesn't re-open it.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('hire') === '1') {
        // Agent requisition emails deep-link with the requested dept/role
        // prefilled (aiinak/request_new_agent) — owner just picks the user
        // account and confirms.
        const dept = params.get('dept');
        const role = params.get('role');
        // A bare hire=1 (no dept) is the business-setup deep link — a brand
        // new customer. Send them through the guided onboarding wizard.
        if (!dept) {
          navigate('/onboarding', { replace: true });
          return;
        }
        setShowCreateModal(true);
        if (dept) {
          setNewAgent(prev => ({ ...prev, department: dept, roleKey: role || '' }));
        }
        params.delete('dept');
        params.delete('role');
        params.delete('hire');
        const qs = params.toString();
        window.history.replaceState({}, document.title, window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (_) { /* no-op */ }
  }, []);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/ai/agents');
      setAgents(response.data.data || []);
    } catch (error) {
      logger.error('Error fetching agents:', error);
      showNotification?.('Failed to load agents', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axiosInstance.get('/api/business/users');
      // Filter for active users that are NOT already AI agents
      const activeNonAIUsers = (response.data.users || []).filter(u =>
        u.status === 'active' && !u.isAIAgent
      );
      setUsers(activeNonAIUsers);
    } catch (error) {
      logger.error('Error fetching users:', error);
      showNotification?.('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchRoleCatalog = async () => {
    try {
      const response = await axiosInstance.get('/api/ai/role-catalog');
      setRoleCatalog(response.data.data || []);
    } catch (error) {
      logger.error('Error fetching role catalog:', error);
      // Non-fatal: the Role select just shows no options; hire still works.
    }
  };

  const seedDefaultAgents = async () => {
    try {
      await axiosInstance.post('/api/ai/agents/seed');
      fetchAgents();
      showNotification?.('Default agents created successfully', 'success');
    } catch (error) {
      logger.error('Error seeding agents:', error);
      showNotification?.('Failed to create default agents', 'error');
    }
  };

  const createAgent = async () => {
    try {
      setCreating(true);
      const response = await axiosInstance.post('/api/ai/agents', newAgent);
      fetchAgents();
      fetchUsers(); // Refresh users list to remove newly assigned agent
      setShowCreateModal(false);
      setNewAgent({
        agentName: '',
        userId: '',
        department: 'support',
        roleKey: '',
        description: ''
      });

      // Check billing result and show appropriate notification
      const billing = response.data?.billing;
      if (billing) {
        if (billing.charged) {
          // Hiring charges the full first period (see admin-service
          // config/agentPricing.js) — the old "(prorated)" wording came from a
          // removed billing path and misreported the amount.
          const amount = typeof billing.amount === 'number' ? ` Charged $${billing.amount.toFixed(2)}.` : '';
          showNotification?.(`Agent created successfully.${amount}`, 'success');
        } else if (billing.reason) {
          showNotification?.(`Agent created, but billing failed: ${billing.reason}. Please update your payment method.`, 'warning');
        } else {
          showNotification?.('Agent created successfully', 'success');
        }
      } else {
        showNotification?.('Agent created successfully', 'success');
      }
    } catch (error) {
      logger.error('Error creating agent:', error);
      showNotification?.(error.response?.data?.message || 'Failed to create agent', 'error');
    } finally {
      setCreating(false);
    }
  };

  const toggleAgent = async (agentId, currentStatus) => {
    try {
      const endpoint = currentStatus === 'active' ? 'pause' : 'activate';
      await axiosInstance.post(`/api/ai/agents/${agentId}/${endpoint}`);
      fetchAgents();
      showNotification?.('Agent status updated', 'success');
    } catch (error) {
      logger.error('Error toggling agent:', error);
      showNotification?.('Failed to update agent status', 'error');
    }
  };

  const deleteAgent = async (agentId, agentName) => {
    if (!window.confirm(`Are you sure you want to delete "${agentName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/ai/agents/${agentId}`);
      fetchAgents();
      fetchUsers(); // Refresh users list to include previously assigned user
      showNotification?.('Agent deleted successfully', 'success');
    } catch (error) {
      logger.error('Error deleting agent:', error);
      showNotification?.(error.response?.data?.message || 'Failed to delete agent', 'error');
    }
  };

  // Filtered agents based on search and department
  const filteredAgents = agents.filter(agent => {
    const matchesSearch = !searchQuery ||
      agent.agentName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.emailConfig?.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      agent.department?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDept = departmentFilter === 'all' || agent.department === departmentFilter;
    return matchesSearch && matchesDept;
  });

  // Stats
  const activeCount = agents.filter(a => a.status === 'active').length;
  const inactiveCount = agents.length - activeCount;
  const voiceEnabledCount = agents.filter(a => a.voiceConfig?.enabled).length;
  const uniqueDepts = [...new Set(agents.map(a => a.department))].length;

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Agents
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {agents.length} {agents.length === 1 ? 'agent' : 'agents'} configured
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => users.length ? setShowCreateModal(true) : navigate('/onboarding')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Assign Agent Role
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {agents.length > 0 && (
        <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Total</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{agents.length}</span>
              </div>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-700" />
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Active</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{activeCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Inactive</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{inactiveCount}</span>
              </div>
              <div className="w-px h-4 bg-gray-300 dark:bg-gray-700" />
              <div className="flex items-center gap-2">
                <PhoneIcon className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Voice Enabled</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{voiceEnabledCount}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Departments</span>
                <span className="text-sm font-semibold text-gray-900 dark:text-white">{uniqueDepts}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      {agents.length > 0 && (
        <div className="border-b border-gray-200 dark:border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-3">
            <div className="flex items-center gap-4">
              {/* Search Input */}
              <div className="relative flex-1 max-w-sm">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search agents..."
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent placeholder:text-gray-400"
                />
              </div>

              {/* Department Filter Pills */}
              <div className="flex items-center gap-1.5 overflow-x-auto">
                {DEPARTMENTS.filter(d => d === 'all' || agents.some(a => a.department === d)).map(dept => (
                  <button
                    key={dept}
                    onClick={() => setDepartmentFilter(dept)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                      departmentFilter === dept
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900'
                        : 'bg-gray-100 dark:bg-[#222222] text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {dept === 'all' ? 'All' : dept.charAt(0).toUpperCase() + dept.slice(1)}
                    {dept !== 'all' && (
                      <span className="ml-1 opacity-70">
                        {agents.filter(a => a.department === dept).length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {agents.length === 0 ? (
          <div className="text-center py-12">
            <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No agents assigned yet</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Assign existing user accounts (like hr@company.com, finance@company.com) as AI department agents
            </p>
            <button
              onClick={() => users.length ? setShowCreateModal(true) : navigate('/onboarding')}
              className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              Assign First Agent
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredAgents.length === 0 && (
              <div className="col-span-full text-center py-8">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No agents match your filters.
                </p>
              </div>
            )}
            {filteredAgents.map((agent) => (
              <div
                key={agent._id}
                className="group relative border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md transition-all duration-200"
              >
                {/* Status Banner */}
                <div className={`h-0.5 w-full ${agent.status === 'active' ? 'bg-green-500' : 'bg-gray-300 dark:bg-[#222222]'}`} />

                <div className="p-4">
                  {/* Header with Icon and Status */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] flex items-center justify-center">
                        <Cog6ToothIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                      </div>
                      {/* Status Indicator Dot */}
                      <div className={`absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800 ${
                        agent.status === 'active' ? 'bg-green-500' : 'bg-gray-400'
                      }`} />
                    </div>

                    {/* Department Badge */}
                    <div className="px-2.5 py-0.5 bg-gray-100 dark:bg-[#222222] rounded-md">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 capitalize">
                        {agent.department}
                      </span>
                    </div>
                  </div>

                  {/* Agent Info */}
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                      {agent.agentName}
                    </h3>
                    <div className="flex items-center gap-1.5">
                      <EnvelopeIcon className="w-3 h-3 text-gray-400" />
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {agent.emailConfig?.address}
                      </p>
                    </div>
                  </div>

                  {/* Channel Indicators */}
                  <div className="flex items-center gap-1.5 mb-3">
                    {/* Email Channel */}
                    <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
                      <EnvelopeIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                      <span className="text-xs font-medium text-blue-700 dark:text-blue-300">Email</span>
                    </div>

                    {/* Voice Channel */}
                    {agent.voiceConfig?.enabled ? (
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                        <PhoneIcon className="w-3 h-3 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-medium text-green-700 dark:text-green-300">
                          Voice{agent.voiceConfig?.phoneNumbers?.length > 0 && ` (${agent.voiceConfig.phoneNumbers.length})`}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded opacity-50">
                        <PhoneIcon className="w-3 h-3 text-gray-400" />
                        <span className="text-xs font-medium text-gray-500">Voice</span>
                      </div>
                    )}

                    {/* Intents Count */}
                    {agent.enabledIntents && agent.enabledIntents.length > 0 && (
                      <div className="ml-auto flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {agent.enabledIntents.length}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Description Preview */}
                  {agent.description && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">
                      {agent.description}
                    </p>
                  )}

                  {/* Interaction Stats */}
                  {(agent.contextStore?.totalEmails > 0 || agent.contextStore?.totalCalls > 0) && (
                    <div className="flex items-center gap-3 mb-3 text-xs text-gray-500 dark:text-gray-400">
                      {agent.contextStore?.totalEmails > 0 && (
                        <span>{agent.contextStore.totalEmails} emails</span>
                      )}
                      {agent.contextStore?.totalCalls > 0 && (
                        <span>{agent.contextStore.totalCalls} calls</span>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-3 gap-1.5">
                    <button
                      onClick={() => navigate(`/ai-agent-config/${agent._id}`)}
                      className="px-2 py-1.5 text-xs font-medium rounded transition-all bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 flex items-center justify-center gap-1"
                      title="Configure Agent"
                    >
                      <Cog6ToothIcon className="w-3.5 h-3.5" />
                      <span className="hidden lg:inline">Config</span>
                    </button>

                    <button
                      onClick={() => toggleAgent(agent._id, agent.status)}
                      className={`px-2 py-1.5 text-xs font-medium rounded transition-all flex items-center justify-center gap-1 ${
                        agent.status === 'active'
                          ? 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300'
                      }`}
                      title={agent.status === 'active' ? 'Pause Agent' : 'Activate Agent'}
                    >
                      <span className="hidden lg:inline">{agent.status === 'active' ? 'Pause' : 'Active'}</span>
                      <span className="lg:hidden text-sm">{agent.status === 'active' ? '⏸' : '▶'}</span>
                    </button>

                    <button
                      onClick={() => deleteAgent(agent._id, agent.agentName)}
                      className="px-2 py-1.5 text-xs font-medium rounded transition-all bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 flex items-center justify-center gap-1"
                      title="Delete Agent"
                    >
                      <TrashIcon className="w-3.5 h-3.5" />
                      <span className="hidden lg:inline">Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
          onClick={() => !creating && setShowCreateModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-lg transform transition-all animate-slideUp"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Assign AI Agent Role
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Assign an existing user account as an AI department agent
                  </p>
                </div>
                <button
                  onClick={() => !creating && setShowCreateModal(false)}
                  disabled={creating}
                  className="ml-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5 max-h-[calc(100vh-16rem)] overflow-y-auto">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Agent Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newAgent.agentName}
                  onChange={(e) => setNewAgent({ ...newAgent, agentName: e.target.value })}
                  placeholder="e.g., HR Assistant"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Select User <span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  value={newAgent.userId}
                  onChange={(value) => setNewAgent({ ...newAgent, userId: value })}
                  options={users.map(user => ({
                    value: user._id || user.id,
                    label: `${user.firstName} ${user.lastName} (${user.email})`
                  }))}
                  placeholder={loadingUsers ? 'Loading users...' : users.length === 0 ? 'No users available' : 'Select a user'}
                  disabled={loadingUsers || users.length === 0}
                />
                {users.length === 0 && !loadingUsers && (
                  <p className="mt-1 text-xs text-orange-600 dark:text-orange-400">
                    No available users found. Please create a user first or all users are already assigned as AI agents.
                  </p>
                )}
                {newAgent.userId && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Selected: {users.find(u => (u._id || u.id) === newAgent.userId)?.email}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Department <span className="text-red-500">*</span>
                </label>
                <select
                  value={newAgent.department}
                  onChange={(e) => setNewAgent({ ...newAgent, department: e.target.value, roleKey: '' })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all cursor-pointer"
                >
                  <option value="hr">HR</option>
                  <option value="finance">Finance</option>
                  <option value="it">IT</option>
                  <option value="sales">Sales</option>
                  <option value="support">Support</option>
                  <option value="legal">Legal</option>
                  <option value="operations">Operations</option>
                  <option value="marketing">Marketing</option>
                  <option value="management">Management</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Role <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <select
                  value={newAgent.roleKey}
                  onChange={(e) => setNewAgent({ ...newAgent, roleKey: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all cursor-pointer"
                >
                  <option value="">No specific role</option>
                  {roleCatalog
                    .filter(r => r.department === newAgent.department)
                    .map(r => (
                      <option key={r.roleKey} value={r.roleKey}>
                        {r.title} ({{ junior: 'Junior', senior: 'Senior', lead: 'Lead', manager: 'Manager', director: 'Director', vp: 'VP', c_level: 'C-Level' }[r.level] || r.level})
                      </option>
                    ))}
                </select>
                {newAgent.roleKey && (
                  <p className="mt-1.5 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    {roleCatalog.find(r => r.department === newAgent.department && r.roleKey === newAgent.roleKey)?.jd}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-gray-400 text-xs font-normal">(optional)</span>
                </label>
                <textarea
                  value={newAgent.description}
                  onChange={(e) => setNewAgent({ ...newAgent, description: e.target.value })}
                  placeholder="What does this agent do?"
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 transition-all resize-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 rounded-b-xl flex items-center justify-between gap-3">
              <button
                onClick={() => {
                  if (!creating) {
                    setShowCreateModal(false);
                    setNewAgent({
                      agentName: '',
                      userId: '',
                      department: 'support',
                      description: ''
                    });
                  }
                }}
                disabled={creating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={createAgent}
                disabled={creating || !newAgent.agentName || !newAgent.userId}
                className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gray-900 to-gray-800 dark:from-white dark:to-gray-100 text-white dark:text-gray-900 text-sm font-semibold rounded-lg hover:from-gray-800 hover:to-gray-700 dark:hover:from-gray-100 dark:hover:to-white transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {creating ? 'Assigning Agent Role...' : 'Assign Agent Role'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgents;
